import React from "react";
import { Link } from "react-router-dom";

const Reporting = () => {
  const principles = [
    {
      number: "1",
      title: "Free Media & Democratic Society",
      content:
        "Free media plays an important role in the preservation of democratic society – all over the world. It fulfills a central societal function and contributes to the process of forming public opinion. To do justice to this duty, Citizen Reporters and Professional journalists often have special authorization to conduct research.",
    },
    {
      number: "2",
      title: "Truthful Reporting",
      content:
        "Reporting serves to spread information and must be done truthfully. A Citizen Reporters and Professional journalist's personal judgments must be clearly recognizable as such.",
    },
    {
      number: "3",
      title: "Right to Respond",
      content:
        "People or organizations affected by news coverage must have the chance to comment on possible accusations before they are published.",
    },
    {
      number: "4",
      title: "Error Correction",
      content:
        "If, despite conscientious research, a mistake is made, the journalist must point it out and correct it immediately and without being asked.",
    },
    {
      number: "5",
      title: "Against Sensationalism",
      content:
        "The NAI does not approve of sensationalistic reporting in which lurid descriptions of violence, catastrophes, personal tragedies or sexuality are included at the expense of accurateness and objectivity.",
    },
    {
      number: "6",
      title: "Rumor Identification",
      content:
        "If a journalist prints rumors which cannot be verified, these must be clearly recognizable as such.",
    },
    {
      number: "7",
      title: "Sensitive Topics & Trials",
      content:
        "Particular care has to be taken with sensitive topics such as trials. Naming suspects is only permitted in exceptional cases.",
    },
    {
      number: "8",
      title: "Medical Reporting",
      content:
        "Descriptions of medical innovations should be described so unemotionally that patients don't get false hope when reading them.",
    },
    {
      number: "9",
      title: "Financial Reporting",
      content:
        "Particular care must also be taken with topics regarding capital markets and financial products.",
    },
    {
      number: "10",
      title: "Personal Rights & Public Interest",
      content:
        "Members of the NAI respect personal rights and balance their protection with the public interest, even when this interest is justifiable.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Principles of Reporting
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-4xl mx-auto">
              NAI Code of Ethics for Citizen Reporters and Professional
              Journalists
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              NAI Newsmedia Association of India has adopted a code of ethics in
              collaboration with its Citizen reporters of NAI members. In
              essence, the subjects covered include research, personal rights,
              truthful reporting and the separation of editorial content and
              advertisements.
            </p>

            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              The Association's use of this code aims to sensitize every member
              to ethical problems to a greater degree. The NAI code of ethics
              has been developed in such a way to make it binding for every
              member personally, regardless of which medium is being used.
            </p>
          </div>
        </div>
      </section>

      {/* Preamble Section */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Preamble
              </h2>
              <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full"></div>
            </div>

            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="mb-6 leading-relaxed">
                The goal of the code of ethics is to clarify to every citizen
                reporters and Members his responsibility to media consumers and
                to appeal to his obligation to report professional and
                well-founded news.
              </p>

              <p className="mb-6 leading-relaxed">
                Measures are taken against anyone who violates the ethics code.
                These measures range from a warning to expulsion from the
                association. The NAI does not aim to overly regulate its members
                with this code; it is meant to serve as an aid in decision
                making and a guideline for answering ethical questions which
                journalists are continually confronted with. Disputes regarding
                press law can be avoided by the use of this code.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Principles Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Principles of Reporting
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The fundamental principles that guide ethical journalism and
              responsible reporting
            </p>
            <div className="w-24 h-1 bg-blue-600 mx-auto mt-6 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <div
                key={principle.number}
                className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-200 group"
              >
                <div className="flex items-start space-x-6">
                  {/* Number Circle */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-white font-bold text-xl">
                        {principle.number}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                      {principle.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {principle.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Code Enforcement Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white bg-opacity-10 rounded-2xl p-8 backdrop-blur-sm">
            <h2 className="text-3xl font-bold mb-6">
              Code Enforcement & Compliance
            </h2>
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="mb-6 text-blue-100 leading-relaxed">
                The NAI code of ethics is binding for all members and serves as
                a framework for responsible journalism. Violations are taken
                seriously and may result in disciplinary actions ranging from
                warnings to expulsion from the association.
              </p>

              <p className="text-blue-100 leading-relaxed">
                This code is designed to assist journalists in making ethical
                decisions and serves as a guideline for addressing the complex
                ethical questions that arise in daily reporting. By adhering to
                these principles, journalists can maintain public trust and
                avoid legal disputes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Additional Resources
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore more about NAI's ethical guidelines and professional
              standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Code of Ethics",
                description:
                  "Complete NAI Code of Ethics document with detailed guidelines",
                link: "/code-of-ethics",
                icon: "📜",
              },
              {
                title: "Research Guidelines",
                description:
                  "Standards and best practices for journalistic research",
                link: "/research",
                icon: "🔍",
              },
              {
                title: "Media Rights",
                description:
                  "Understanding press freedom and legal protections",
                link: "/media-rights",
                icon: "⚖️",
              },
            ].map((resource, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow duration-300 border border-gray-100"
              >
                <div className="text-4xl mb-4">{resource.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {resource.title}
                </h3>
                <p className="text-gray-600 mb-4">{resource.description}</p>
                <a
                  href={resource.link}
                  className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold"
                >
                  Learn More
                  <svg
                    className="w-4 h-4 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Commit to Ethical Journalism
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join NAI in promoting responsible reporting and upholding the
            highest standards of journalistic integrity.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            
            <Link to="/apply" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300">
              Join NAI
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reporting;
